import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { LayoutDashboard, Users, ClipboardList, FileText, LogOut, UserCog, AlertOctagon, Menu, X, BarChart3, CalendarDays } from 'lucide-react';
import ThemeToggle from './ThemeToggle';

export default function Layout({ user, onLogout }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/drivers', label: 'Drivers', icon: Users },
    { path: '/assessments', label: 'Assessments', icon: ClipboardList },
    { path: '/coach-scheduler', label: 'ADC Scheduler', icon: CalendarDays },
    { path: '/analytics', label: 'Analytics', icon: BarChart3 },
    // Reports moved to per-driver report button

  ];

  // VCR hidden from menu (not required yet — code remains intact)
  // if (user.role === 'admin' || user.role === 'manager') {
  //   navItems.push({ path: '/vcr', label: 'VCR', icon: AlertOctagon });
  // }
  
  // Add Users menu only for admin
  if (user.role === 'admin') {
    navItems.push({ path: '/users', label: 'Users', icon: UserCog });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <nav className="bg-white dark:bg-slate-800 shadow-md border-b border-blue-100 dark:border-slate-700 no-print relative z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center space-x-4">
              <div>
                <h1 className="text-lg font-bold leading-tight">
                  <span className="text-green-600 dark:text-green-400">ADC</span>
                  <span className="text-slate-800 dark:text-slate-100"> Portal</span>
                </h1>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-normal -mt-1"></p>
              </div>
              <div className="hidden lg:flex space-x-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link key={item.path} to={item.path}>
                      <Button
                        variant={isActive ? 'default' : 'ghost'}
                        className="flex items-center space-x-2"
                        data-testid={`nav-${item.label.toLowerCase()}`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{item.label}</span>
                      </Button>
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {/* Theme Toggle */}
              <ThemeToggle />
              
              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
              
              <span className="text-sm text-slate-600 dark:text-slate-300 hidden sm:block" data-testid="user-info">
                {user.username} ({user.role})
              </span>
              <Button variant="outline" size="sm" onClick={onLogout} data-testid="logout-button" className="hidden sm:flex">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
          
          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-slate-200 bg-white">
              <div className="px-2 py-3 space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}>
                      <Button
                        variant={isActive ? 'default' : 'ghost'}
                        className="w-full justify-start"
                        data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                      >
                        <Icon className="h-4 w-4 mr-2" />
                        {item.label}
                      </Button>
                    </Link>
                  );
                })}
                <Button
                  variant="outline"
                  className="w-full justify-start mt-2"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onLogout();
                  }}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
}